/**
 * generateMeshSvg – renders a triangulation result as an SVG string,
 * matching the style of clocation01b.svg:
 *   – black background
 *   – dark-green triangle edges
 *   – blue dots for material vertices, per-hole colour for hole vertices
 *   – bright-yellow dots for contour-boundary vertices
 *   – tiny centroid dots coloured by triangle region
 */

import { TriangulationResult } from '../types';

const SVG_SIZE  = 900;

const EDGE_COLOR            = '#004400';
const EDGE_CUT_BOUNDARY     = '#cc4400';   // krawędź granicy cięcia (czerwona → czarna)
const CENTROID_MATERIAL     = '#00003a';
const CENTROID_CUT          = '#662200';   // centroid trójkąta do wycięcia
const NODE_MATERIAL         = '#2244cc';
const NODE_OUTER_BOUNDARY   = '#00aaaa';
const NODE_HOLE_BOUNDARY    = '#cccc00';

// Per-opening colour palette (centroid dots + interior nodes)
const OPENING_COLORS = [
  '#cc3333',
  '#996600',
  '#cc33cc',
  '#33aacc',
  '#33cc66',
];

function openingColor(idx: number): string {
  return OPENING_COLORS[idx % OPENING_COLORS.length];
}

export function generateMeshSvg(
  mesh:     TriangulationResult,
  widthMm:  number,
  heightMm: number,
): string {
  const scale = SVG_SIZE / Math.max(widthMm, heightMm);
  const padX  = (SVG_SIZE - widthMm  * scale) / 2;
  const padY  = (SVG_SIZE - heightMm * scale) / 2;

  const tx = (x: number) => (padX + (x + widthMm  / 2) * scale).toFixed(1);
  const ty = (y: number) => (padY + (-y + heightMm / 2) * scale).toFixed(1);

  const parts: string[] = [];

  parts.push(
    `<svg xmlns="http://www.w3.org/2000/svg" ` +
    `width="${SVG_SIZE}" height="${SVG_SIZE}">`,
    `<rect width="${SVG_SIZE}" height="${SVG_SIZE}" fill="#000"/>`,
  );

  // ── Edges (deduplicated) ──────────────────────────────────
  // Najpierw budujemy mapę krawędź → triCutLabels obu sąsiadujących trójkątów,
  // żeby wykryć krawędzie granicy cięcia (cutLabel 0↔1).
  const V = mesh.vertices;
  const hasCutLabels = mesh.triCutLabels?.length > 0;

  // Mapa: key → { first: cutLabel trójkąta który dodał, count: ile trójkątów }
  const edgeCutMap = new Map<number, { cut0: boolean; cut1: boolean }>();
  if (hasCutLabels) {
    for (let t = 0; t < mesh.triangles.length; t++) {
      const [i, j, k] = mesh.triangles[t];
      const isCut = mesh.triCutLabels[t] === 1;
      for (const [a, b] of [[i, j], [j, k], [k, i]] as [number, number][]) {
        const lo = Math.min(a, b), hi = Math.max(a, b);
        const key = lo * 100003 + hi;
        const cur = edgeCutMap.get(key) ?? { cut0: false, cut1: false };
        if (isCut) cur.cut1 = true; else cur.cut0 = true;
        edgeCutMap.set(key, cur);
      }
    }
  }

  const seen = new Set<number>();
  const edgeNormal: string[] = [];
  const edgeCut: string[] = [];

  for (const [i, j, k] of mesh.triangles) {
    for (const [a, b] of [[i, j], [j, k], [k, i]] as [number, number][]) {
      const lo = Math.min(a, b), hi = Math.max(a, b);
      const key = lo * 100003 + hi;
      if (!seen.has(key)) {
        seen.add(key);
        const isCutBoundary = hasCutLabels &&
          ((edgeCutMap.get(key)?.cut0 && edgeCutMap.get(key)?.cut1) ?? false);
        const line =
          `<line x1="${tx(V[a].x)}" y1="${ty(V[a].y)}" ` +
          `x2="${tx(V[b].x)}" y2="${ty(V[b].y)}" ` +
          `stroke="${isCutBoundary ? EDGE_CUT_BOUNDARY : EDGE_COLOR}" ` +
          `stroke-width="${isCutBoundary ? 1.5 : 0.5}"/>`;
        if (isCutBoundary) edgeCut.push(line); else edgeNormal.push(line);
      }
    }
  }
  // Najpierw normalne krawędzie, potem granica cięcia na wierzchu
  parts.push('<g>', ...edgeNormal, '</g>');
  if (edgeCut.length > 0) parts.push('<g>', ...edgeCut, '</g>');

  // ── Centroid dots ─────────────────────────────────────────
  const centroidParts: string[] = [];
  for (let t = 0; t < mesh.triangles.length; t++) {
    const [i, j, k] = mesh.triangles[t];
    const cx = tx((V[i].x + V[j].x + V[k].x) / 3);
    const cy = ty((V[i].y + V[j].y + V[k].y) / 3);
    const lbl    = mesh.triLabels[t];
    const isCut  = hasCutLabels && mesh.triCutLabels[t] === 1;
    const col = isCut
      ? CENTROID_CUT
      : lbl === 0 ? CENTROID_MATERIAL : openingColor(lbl - 1);
    centroidParts.push(`<circle cx="${cx}" cy="${cy}" r="1" fill="${col}"/>`);
  }
  parts.push('<g>', ...centroidParts, '</g>');

  // ── Vertex dots ───────────────────────────────────────────
  const nodeParts: string[] = [];
  for (let v = 0; v < V.length; v++) {
    const x   = tx(V[v].x), y = ty(V[v].y);
    const nl  = mesh.nodeLabels[v];
    const reg = mesh.nodeRegions[v];

    let color: string;
    let r: number;

    if (nl === 1) {
      // On outer (material) boundary
      color = NODE_OUTER_BOUNDARY; r = 2;
    } else if (nl >= 2) {
      // On hole boundary
      color = NODE_HOLE_BOUNDARY; r = 2;
    } else if (reg > 0) {
      // Interior node inside an opening
      color = openingColor(reg - 1); r = 1.5;
    } else {
      // Interior node in material
      color = NODE_MATERIAL; r = 1.5;
    }
    nodeParts.push(`<circle cx="${x}" cy="${y}" r="${r}" fill="${color}"/>`);
  }
  parts.push('<g>', ...nodeParts, '</g>');

  parts.push('</svg>');
  return parts.join('\n');
}

export function downloadSvg(filename: string, svgContent: string): void {
  const blob = new Blob([svgContent], { type: 'image/svg+xml' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
